import { FileText, Mail, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import { Dialog, DialogContent } from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  filterRestrictedEmails,
  isRestrictedEmail,
} from '@/shared/constants/restrictedEmails'
import { syncAllAdminData } from '@/shared/lib/crmSync'
import { toast } from '@/shared/lib/toast'

import {
  getAdminQuotationEmailPreview,
  sendAdminQuotation,
} from '../../../admin/api/quotation.api'
import { useQuotationStore } from '../../stores/quotation.store'

const DEFAULT_CC = ['niranjan@superiorsystems.in', 'sachin@superiorsystems.in']

export default function SendQuotationModal({
  open,
  onOpenChange,
  quotation,
  onSuccess,
  isAdmin,
}) {
  const { sendQuotation, getQuotationEmailPreview } = useQuotationStore()

  const [loading, setLoading] = useState(false)

  const [newCc, setNewCc] = useState('')
  const [showCcInput, setShowCcInput] = useState(false)

  const [form, setForm] = useState({
    to: '',
    cc: DEFAULT_CC,
    subject: '',
    body: '',
  })

  useEffect(() => {
    if (!open || !quotation) return

    let cancelled = false

    const leadEmail = quotation.lead?.email || quotation.leadEmail || ''

    const loadPreview = async () => {
      try {
        const previewResponse = isAdmin
          ? await getAdminQuotationEmailPreview(quotation._id)
          : await getQuotationEmailPreview(quotation._id)

        const preview = isAdmin ? previewResponse?.data : previewResponse
        if (cancelled) return

        const rawPreviewCc = preview?.cc
          ? String(preview.cc)
              .split(',')
              .map((email) => email.trim())
              .filter(Boolean)
          : []

        const previewCc = filterRestrictedEmails(rawPreviewCc)

        setForm({
          to: preview?.to || leadEmail || '',
          cc: [...new Set([...DEFAULT_CC, ...previewCc])],
          subject: preview?.subject || '',
          body: preview?.message || '',
        })
      } catch (error) {
        console.error(error)
        if (cancelled) return

        setForm({
          to: leadEmail || '',
          cc: DEFAULT_CC,
          subject: '',
          body: '',
        })
      }
    }

    loadPreview()

    return () => {
      cancelled = true
    }
  }, [open, quotation, getQuotationEmailPreview, isAdmin])

  const addCc = () => {
    const email = newCc.trim()

    if (!email) return

    if (isRestrictedEmail(email)) {
      toast.error('Sending email to this address is restricted')
      setNewCc('')
      return
    }

    if (form.cc.includes(email)) {
      setNewCc('')
      return
    }

    setForm((prev) => ({
      ...prev,
      cc: [...prev.cc, email],
    }))

    setNewCc('')

    setTimeout(() => {
      document.getElementById('cc-input')?.focus()
    }, 0)
  }

  const removeCc = (email) => {
    setForm((prev) => ({
      ...prev,
      cc: prev.cc.filter((e) => e !== email),
    }))
  }

  const handleSend = async () => {
    if (!form.to?.trim()) {
      toast.error('Please enter a recipient email')
      return
    }

    if (isRestrictedEmail(form.to)) {
      toast.error('Sending email to this recipient is restricted')
      return
    }

    const cleanCc = filterRestrictedEmails(form.cc)

    try {
      setLoading(true)

      const payload = {
        to: form.to,
        cc: cleanCc,
        subject: form.subject,
        message: form.body,
      }

      if (isAdmin) {
        await sendAdminQuotation(quotation._id, payload)
      } else {
        await sendQuotation(quotation._id, payload)
      }

      await onSuccess?.()

      syncAllAdminData({ type: 'quotation:sent', quotationId: quotation._id })

      onOpenChange(false)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
        w-[calc(100%-16px)]
        max-w-2xl
        max-h-[90vh]
        overflow-y-auto
        rounded-3xl
        p-4
        sm:w-full
        sm:p-6
        [&>button]:hidden
      "
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Mail className="h-5 w-5 shrink-0" />

            <h2 className="text-lg font-semibold sm:text-xl">Send Quotation</h2>
          </div>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="shrink-0 rounded-md p-1 hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-4 sm:mt-5 sm:space-y-5">
          {/* To */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              To
            </Label>

            <Input
              value={form.to}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, to: e.target.value }))
              }
              placeholder="Enter recipient email"
              className="mt-2 h-12 w-full rounded-full"
            />
            {isRestrictedEmail(form.to) && (
              <p className="mt-1.5 text-xs font-medium text-rose-500">
                Sending email to this recipient is restricted.
              </p>
            )}
          </div>

          {/* CC */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              CC (AUTO-INCLUDED)
            </Label>

            <div className="mt-2 flex max-h-[150px] min-h-[58px] w-full flex-wrap items-start gap-2 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 sm:max-h-none sm:p-3">
              {form.cc.map((email) => (
                <div
                  key={email}
                  className="
                  flex
                  max-w-full
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-slate-200
                  bg-white
                  px-3
                  py-1.5
                  text-xs
                  shadow-sm
                  sm:px-4
                  sm:text-sm
                "
                >
                  <span className="min-w-0 truncate">{email}</span>

                  <button
                    type="button"
                    onClick={() => removeCc(email)}
                    className="shrink-0"
                  >
                    <X className="h-3.5 w-3.5 text-slate-500" />
                  </button>
                </div>
              ))}

              {showCcInput ? (
                <input
                  id="cc-input"
                  autoFocus
                  value={newCc}
                  onChange={(e) => setNewCc(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addCc()
                    }
                  }}
                  placeholder="Add email and enter"
                  className="
                  min-w-[180px]
                  max-w-full
                  flex-1
                  rounded-full
                  border
                  border-slate-200
                  bg-white
                  px-3
                  py-1.5
                  text-xs
                  outline-none
                  sm:text-sm
                "
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowCcInput(true)}
                  className="px-1 text-sm text-slate-400 hover:text-slate-600"
                >
                  + Add email
                </button>
              )}
            </div>
          </div>

          {/* Subject */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Subject
            </Label>

            <Input
              value={form.subject}
              readOnly
              className="mt-2 h-12 w-full rounded-full font-medium"
            />
          </div>

          {/* Message */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Message
            </Label>

            <textarea
              rows={8}
              value={form.body}
              readOnly
              className="
              mt-2
              min-h-[180px]
              max-h-[280px]
              w-full
              resize-none
              overflow-y-auto
              rounded-2xl
              border
              p-3
              text-sm
              outline-none
              sm:min-h-[220px]
            "
            />
          </div>

          {/* Attachment Info */}
          <div className="flex items-start gap-3 rounded-2xl bg-slate-50 px-3 py-3 sm:px-4">
            <FileText className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />

            <p className="text-xs leading-5 text-slate-500 sm:text-sm">
              <span className="font-medium text-slate-700">
                PDF of {quotation?.quoteNo}
              </span>{' '}
              (₹
              {quotation?.grandTotal?.toLocaleString('en-IN')}) will be attached
              automatically.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="w-full rounded-full sm:w-auto"
          >
            Cancel
          </Button>

          <Button
            onClick={handleSend}
            disabled={loading || !form.to?.trim() || isRestrictedEmail(form.to)}
            className="w-full rounded-full sm:w-auto bg-[#0F172A] hover:bg-[#0F172A]"
          >
            {loading ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
